import type { Shape } from "../adapters/types";
import type {
  Command,
  History,
  HistoryChangeKind,
} from "./History";
import type { ShapeStore } from "./ShapeStore";

export interface SaveEvent {
  /**
   * Forme(s) après la modification ("create"/"update"), ou leur dernier état connu avant
   * suppression ("delete" — la forme n'existe déjà plus dans `shapeStore` à cet instant). TOUJOURS
   * un tableau, même pour une modification à une seule forme (`run()` — voir `trackAndSave`) :
   * `shapes.length === 1` dans ce cas. Une modification de GROUPE (sélection multiple, BESOINS.md
   * §13, `runBatch()` — voir `trackAndSaveBatch`) y porte TOUTES les formes touchées par le même
   * geste/la même action de groupe (déplacement, verrouillage, suppression...), normalement livrées
   * en UN seul appel `onSave` plutôt qu'un par forme — le consommateur y a accès à l'ensemble du
   * groupe pour, par exemple, les persister en une seule requête réseau. `kind` est le même pour
   * toutes les formes d'un même appel (jamais un mélange au sein d'un même `shapes` — voir
   * `BatchSaveEntry.kind`) : `"update"` pour un déplacement/une rotation/un redimensionnement/une
   * modification de groupe (la sélection multiple à la souris ne crée/ne supprime jamais de forme),
   * `"delete"` pour une suppression de groupe (voir `deleteShape` dans `createTokaMap.ts`). Exception :
   * un groupe à kinds mixtes ("diviser ici" sur une ligne — `"update"`+`"create"`, inversés en
   * `"update"`+`"delete"` par Annuler/Rétablir) donne lieu à PLUSIEURS appels `onSave`, un par kind
   * distinct présent dans le groupe (voir `ShapeSaveTracker.trackAndSaveBatch`) — chaque appel reste
   * individuellement homogène en `kind`.
   */
  shapes: Shape[];
  kind: "create" | "update" | "delete";
}

export type OnSave = (event: SaveEvent) => Promise<void>;

/**
 * "pending" : une sauvegarde est en cours pour cette forme (peut être émis plusieurs fois de
 * suite tant que d'autres modifications s'enchaînent avant que la file ne se vide).
 * "success"/"error" : plus aucune sauvegarde en vol pour cette forme, avec le résultat final —
 * un échec superposé qui n'est pas le plus récent (voir `ShapeSaveTracker.run`) n'émet rien, ce
 * n'est pas encore le résultat final de cette forme.
 */
export interface SaveStatusEvent {
  shapeId: string;
  status: "pending" | "success" | "error";
}

export type SaveStatusListener = (event: SaveStatusEvent) => void;

/** Enveloppe une `Command` avec une identité propre à CE suivi : une même commande peut être
 * suivie plusieurs fois (ex : sa propre exécution, puis un `undo()` manuel avant même que la
 * première sauvegarde n'ait résolu — voir `runHistoryChange`) ; `indexOf` sur des `Command` brutes
 * ne pourrait pas distinguer ces deux suivis du même objet. */
interface PendingEntry {
  command: Command;
}

interface ShapeSaveTracking {
  confirmedShape: Shape | null;
  /** Suivis en vol depuis `confirmedShape`, du plus ancien au plus récent. */
  pendingCommands: PendingEntry[];
}

/** Une entrée par forme touchée par une commande — `run()` en capture toujours exactement une,
 * `runBatch()` une par forme du groupe (voir `CommandMeta.entries`). */
interface CommandMetaEntry {
  shapeId: string;
  /** Nature de la commande ORIGINALE (celle de son exécution, pas d'un futur undo/redo). */
  kind: SaveEvent["kind"];
  /** JSON de la forme juste avant l'exécution originale (`null` pour une création). */
  beforeShape: Shape | null;
  /** JSON de la forme juste après l'exécution originale (`null` pour une suppression). */
  afterShape: Shape | null;
}

/** Métadonnées capturées à l'exécution d'une commande (voir `run`/`runBatch`), pour pouvoir la
 * refaire suivre correctement si elle est plus tard manuellement annulée/rétablie (voir
 * `runHistoryChange`) — sans quoi Annuler/Rétablir n'appelleraient jamais `onSave`. */
interface CommandMeta {
  /** Une entrée par forme concernée — longueur 1 pour une commande `run()` (forme unique), N pour
   * une commande `runBatch()` (`BatchCommand`, sélection multiple, voir `commands.ts`). */
  entries: CommandMetaEntry[];
  skipSave: boolean;
  /** Voir `ShapeSaveTracker.run` — reporté sur `runHistoryChange` pour qu'un undo/redo manuel de
   * cette commande n'en fasse pas non plus une référence confirmée. */
  skipConfirm: boolean;
  /** `true` si cette commande vient de `runBatch` : `runHistoryChange` doit alors regrouper les
   * `entries` par kind et appeler `onSave({ shapes: [...celles du kind], kind })` une fois par kind
   * distinct (un seul appel si toutes partagent le même kind), plutôt qu'un appel
   * `onSave({ shapes: [une seule], kind })` par forme (voir `trackAndSaveBatch`/`trackAndSave`). */
  batch: boolean;
}

/** Une forme à suivre/persister au sein d'une commande de groupe (voir `runBatch`). */
export interface BatchSaveEntry {
  shapeId: string;
  kind: SaveEvent["kind"];
  /** Voir `run`'s `confirmedShapeIfNew` — même rôle, par forme du groupe. */
  confirmedShapeIfNew: Shape | null;
  /** Voir `run`'s `resultShape` — même rôle, par forme du groupe. */
  resultShape: Shape | null;
}

/**
 * Suivi de concurrence par forme pour la sauvegarde optimiste avec rollback (BESOINS.md §7.1) —
 * extrait de `createTokaMap` pour être testable indépendamment de MapLibre/DOM.
 *
 * Une seule file par forme (pas par sommet) : une commande "forme entière" (création/suppression/
 * rotation/déplacement) et une commande "sommet" sur la MÊME forme partagent la même file — deux
 * modifications en vol sur la même forme, peu importe leur nature (y compris un Annuler/Rétablir
 * manuel, voir `runHistoryChange`), se succèdent dans la même file.
 *
 * `confirmedShape` retient le dernier JSON complet de la forme confirmé par une sauvegarde
 * réussie (jamais un état intermédiaire encore en vol) — `null` si la forme n'a encore aucune
 * version confirmée (ex : création jamais sauvegardée). Quand une sauvegarde échoue ET qu'aucune
 * autre modification n'est en cours sur cette forme, ce JSON est réinjecté tel quel (ou la forme
 * est supprimée si `null`) plutôt que de tenter un rollback partiel.
 *
 * Peut délibérément rester en retard sur l'état réel du store même quand `pendingCommands` est
 * vide : voir `trackAndSave`, `skipConfirm` (`DrawTriggerOptions.skipConfirm`) — une commande
 * réussie mais marquée `skipConfirm` n'avance jamais cette référence.
 */
export class ShapeSaveTracker {
  private tracking = new Map<string, ShapeSaveTracking>();
  private commandMeta = new WeakMap<Command, CommandMeta>();
  private listeners = new Set<SaveStatusListener>();
  /** Ancre "avant toute la rafale skipHistory en cours" par forme — voir `resolveHistoryBeforeShape`.
   * Concept indépendant de `tracking`/`confirmedShape` ci-dessus (qui suit les sauvegardes réseau,
   * pas ce qu'un `Command.undo()` doit restaurer). */
  private historyAnchors = new Map<string, Shape>();

  constructor(
    private shapeStore: ShapeStore,
    private history: History,
    private onSave: OnSave | undefined,
  ) {}

  onStatusChange(listener: SaveStatusListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Résout l'état de `shapeId` que la PROCHAINE commande doit utiliser comme "avant" (celui que
   * `Command.undo()` doit restaurer) — appelé par `createTokaMap.ts` juste avant de construire
   * cette commande, avec l'état COURANT de la forme (`currentShape`, lu depuis `shapeStore`).
   *
   * Une rafale `skipHistory: true` (ex. les ticks continus d'un slider pendant un drag — voir
   * `SliderInputNumberFeatureStyle` côté consommateur, `onChange` vs `onAfterChange`) ne doit
   * laisser AUCUNE trace dans `History` : chaque commande de la rafale s'applique directement
   * (`command.do()`, jamais `history.execute()`, voir `run`/`runBatch` ci-dessous) sur l'état
   * COURANT (typiquement celui laissé par le tick précédent) — correct pour l'aperçu visuel en
   * direct. Mais SANS ce suivi, la commande RÉELLE qui clôt le geste (`skipHistory: false`, ex.
   * `onAfterChange`) serait construite avec un "avant" lu depuis ce même état courant — déjà mutée
   * par TOUS les ticks intermédiaires, donc quasiment identique à l'état "après" (le dernier tick a
   * déjà appliqué la valeur finale) : `Annuler` ne ferait alors quasiment rien, au lieu de défaire
   * tout le geste d'un coup.
   *
   * `currentShape` est donc capturé comme ancre au TOUT PREMIER `skipHistory: true` de la rafale
   * (jamais réécrite tant que la rafale continue — `historyAnchors.has` le garde), puis restitué
   * (et effacé) à la commande de clôture : celle-ci récupère l'état vraiment antérieur au geste
   * ENTIER comme "avant", quel que soit le nombre de ticks intermédiaires. Sans rafale en cours
   * (cas normal, la grande majorité des appels), renvoie `currentShape` tel quel — comportement
   * inchangé.
   *
   * Une ancre en attente peut aussi devenir PÉRIMÉE sans jamais être consommée ici : un Annuler/
   * Rétablir manuel d'une commande antérieure sur la même forme (`runHistoryChange`), ou un rollback
   * réseau (`trackAndSave`/`trackAndSaveBatch`), réécrivent tous les deux l'état "réel" de la forme
   * en dehors de ce mécanisme — ils invalident donc explicitement l'ancre en attente pour cette
   * forme (voir leurs commentaires) plutôt que de la laisser décrire un état devenu fantôme.
   */
  resolveHistoryBeforeShape(shapeId: string, currentShape: Shape, skipHistory: boolean): Shape {
    if (skipHistory) {
      if (!this.historyAnchors.has(shapeId)) this.historyAnchors.set(shapeId, currentShape);
      return currentShape;
    }
    const anchor = this.historyAnchors.get(shapeId);
    this.historyAnchors.delete(shapeId);
    return anchor ?? currentShape;
  }

  /**
   * Exécute `command` pour la forme `shapeId`, puis déclenche `onSave` en tâche de fond (sauf
   * absence de `onSave`, ou `skipSave`). Ne bloque jamais : la commande s'applique tout de suite,
   * `onSave` tourne en parallèle.
   *
   * `skipHistory` : `command.do()` est appelé directement plutôt que `history.execute(command)` —
   * la modification s'applique mais n'est jamais poussée sur la pile Annuler/Rétablir (`undo()`/
   * `redo()` ne peuvent alors jamais la toucher), et aucune métadonnée n'est capturée pour
   * `runHistoryChange` (qui ne serait de toute façon jamais invoqué pour elle, la commande
   * n'apparaissant dans aucune des deux piles). Indépendant de `skipSave` ci-dessous : les deux
   * peuvent être combinés ou utilisés séparément.
   *
   * `skipSave` : `onSave` n'est jamais appelé pour CETTE commande — mais si aucun suivi n'existe
   * déjà pour cette forme, un suivi "à vide" (sans commande en vol) est quand même créé, seedé avec
   * `confirmedShapeIfNew`, plutôt que de ne rien faire. Sans ça, plusieurs commandes `skipSave`
   * enchaînées (aucune jamais persistée) suivies d'une commande normale qui échoue ne reviendraient
   * qu'avant cette DERNIÈRE commande — pas avant tout l'enchaînement `skipSave`, alors que rien de
   * tout ça n'a jamais été confirmé par le serveur. Voir le corps de la méthode.
   *
   * `skipConfirm` : voir `trackAndSave` — même si `onSave` réussit, cette commande ne devient
   * jamais la référence confirmée de la forme. Sans effet si `skipSave` (rien à confirmer, `onSave`
   * n'est de toute façon jamais appelé).
   *
   * - `confirmedShapeIfNew` : JSON de la forme à retenir comme référence SEULEMENT si aucune
   *   modification n'est déjà en vol sur cette forme (sinon la référence déjà en cours est
   *   conservée : elle correspond à la dernière confirmation réelle, pas à un état intermédiaire).
   * - `resultShape` : JSON résultant de cette commande (`null` pour une suppression), retenu comme
   *   nouvelle référence confirmée si sa sauvegarde réussit (sauf `skipConfirm`).
   *
   * Capture aussi les métadonnées de `command` (forme, nature, `confirmedShapeIfNew`/`resultShape`)
   * pour que `runHistoryChange` puisse la refaire suivre correctement si elle est plus tard
   * manuellement annulée/rétablie — y compris si `skipSave`/`skipConfirm` (pour reporter le même
   * traitement sur cet undo/redo plutôt que de le traiter comme une modification "normale").
   */
  run(
    shapeId: string,
    command: Command,
    kind: SaveEvent["kind"],
    skipSave: boolean,
    skipHistory: boolean,
    skipConfirm: boolean,
    confirmedShapeIfNew: Shape | null,
    resultShape: Shape | null,
    shapeForSave: Shape,
  ): void {
    if (skipHistory) {
      command.do();
    } else {
      this.history.execute(command);
      this.commandMeta.set(command, {
        entries: [{ shapeId, kind, beforeShape: confirmedShapeIfNew, afterShape: resultShape }],
        skipSave,
        skipConfirm,
        batch: false,
      });
    }

    if (skipSave) {
      // Ne PAS se contenter de `return` sans rien faire d'autre : si aucun suivi n'existe déjà pour
      // cette forme, il faut quand même en créer un (sans `onSave`, donc sans jamais l'avancer),
      // seedé avec `confirmedShapeIfNew` — l'état d'avant CETTE commande précise. Sans ça, un
      // enchaînement de plusieurs commandes `skipSave` (ex. plusieurs éditions locales de suite,
      // chacune avec `skipSave: true`) ne laisserait aucune trace : la commande normale suivante
      // recalculerait sa propre `confirmedShapeIfNew` depuis le store COURANT (qui, lui, inclut déjà
      // ces éditions locales) et créerait un suivi à partir de cet état — un échec ultérieur ne
      // reviendrait alors qu'avant la DERNIÈRE commande, pas avant tout l'enchaînement `skipSave`,
      // alors qu'aucune de ces commandes n'a jamais été réellement persistée. Si un suivi existe
      // déjà (une sauvegarde réelle est en cours, ou une commande `skipSave` précédente vient de
      // l'amorcer), on ne le touche pas : sa référence est déjà la bonne.
      if (!this.tracking.has(shapeId)) {
        this.tracking.set(shapeId, { confirmedShape: confirmedShapeIfNew, pendingCommands: [] });
      }
      return;
    }

    this.trackAndSave(shapeId, command, kind, skipConfirm, confirmedShapeIfNew, resultShape, shapeForSave);
  }

  /**
   * Équivalent de `run()` pour une modification de GROUPE (sélection multiple, BESOINS.md §13) :
   * `command` (typiquement une `BatchCommand`, voir `commands.ts`) est exécutée comme une SEULE
   * entrée `History` — un seul `history.undo()`/`redo()` couvre alors tout le groupe — et
   * `onSave({ shapes, kind })` n'est appelé, normalement, qu'UNE FOIS pour l'ensemble de `entries`
   * plutôt qu'un appel par forme (`kind` — voir `BatchSaveEntry.kind` — est le même pour toutes les
   * `entries` dans ce cas : `"update"` pour un déplacement/une modification de groupe, `"delete"`
   * pour une suppression de groupe, voir `deleteShape` dans `createTokaMap.ts`). Un groupe à kinds
   * mixtes ("diviser ici" sur une ligne) donne lieu à un appel par kind distinct — voir
   * `trackAndSaveBatch`.
   *
   * Le suivi/rollback par forme reste néanmoins individuel (même `Map<string, ShapeSaveTracking>`
   * que `run()`) : chaque forme du groupe garde sa propre file `pendingCommands`, dans laquelle la
   * MÊME `PendingEntry` (partagée par toutes les formes de ce groupe) est poussée — ça permet à une
   * modification ultérieure, individuelle ou de groupe, sur UNE SEULE de ces formes de superséder
   * cette entrée pour cette forme précise sans affecter les autres (mêmes règles "plus récent
   * l'emporte" que `trackAndSave`, voir `trackAndSaveBatch`). Puisque `command` est un objet unique
   * partagé, `History.discard(command)` le retire de la pile pour TOUT le groupe dès qu'une seule
   * forme en décide — ce qui est justement ce qui rend le groupe atomique du point de vue de
   * l'historique : soit la commande de groupe reste annulable pour toutes les formes encore
   * concernées, soit elle disparaît entièrement.
   *
   * `skipHistory`/`skipSave` ont le même sens qu'en `run()`, appliqués à TOUT le groupe (pas de
   * granularité par forme) : `skipSave` crée quand même un suivi "à vide" par forme absente, sur le
   * même principe que `run()`.
   */
  runBatch(
    entries: BatchSaveEntry[],
    command: Command,
    skipSave: boolean,
    skipHistory: boolean,
    skipConfirm: boolean,
    shapesForSave: Shape[],
  ): void {
    if (skipHistory) {
      command.do();
    } else {
      this.history.execute(command);
      this.commandMeta.set(command, {
        entries: entries.map((e) => ({
          shapeId: e.shapeId,
          kind: e.kind,
          beforeShape: e.confirmedShapeIfNew,
          afterShape: e.resultShape,
        })),
        skipSave,
        skipConfirm,
        batch: true,
      });
    }

    if (skipSave) {
      for (const e of entries) {
        if (!this.tracking.has(e.shapeId)) {
          this.tracking.set(e.shapeId, { confirmedShape: e.confirmedShapeIfNew, pendingCommands: [] });
        }
      }
      return;
    }

    this.trackAndSaveBatch(entries, command, skipConfirm, shapesForSave);
  }

  /**
   * Réagit à un Annuler/Rétablir manuel (`History`'s `onChange`, `kind: "undo" | "redo"`) sur une
   * commande déjà exécutée via `run` — pour que `onSave` soit aussi appelé dans ce cas (BESOINS.md
   * §7.1), pas seulement pour une modification "fraîche". Sans effet si `command` n'a pas de
   * métadonnées connues (ne devrait pas arriver, toute commande de cette forme passe par `run`), ou
   * si elle a été exécutée avec `skipSave` (reste locale y compris à travers Annuler/Rétablir).
   *
   * Annuler une création équivaut à une suppression (et inversement) du point de vue de `onSave` ;
   * annuler/rétablir une modification reste une modification. Le JSON envoyé et retenu comme
   * nouvelle référence en cas de succès est lu EN DIRECT dans `shapeStore` (pas les valeurs figées
   * de `CommandMeta`) : `undo()`/`redo()` viennent de s'exécuter, et pour les commandes "forme
   * entière" (undo() relatif, voir `commands.ts`) le résultat réel peut différer légèrement de
   * l'instantané original si un sommet a été modifié indépendamment entre-temps.
   */
  runHistoryChange(command: Command, changeKind: Extract<HistoryChangeKind, "undo" | "redo">): void {
    const meta = this.commandMeta.get(command);
    if (!meta) return;
    // Un Annuler/Rétablir manuel vient de réécrire l'état "réel" de ces formes en dehors de
    // `resolveHistoryBeforeShape` — toute ancre encore en attente pour l'une d'elles (une rafale
    // `skipHistory` jamais commitée, ouverte AVANT ce undo/redo) décrit donc un état qui n'a plus
    // cours : la laisser telle quelle ferait construire la PROCHAINE commande réelle sur cette forme
    // avec un "avant" périmé (voir `resolveHistoryBeforeShape`) — un `Annuler` de CETTE commande
    // reviendrait alors à un état fantôme, ni l'original ni le résultat du undo/redo qu'on vient de
    // faire. Avant `meta.skipSave` ci-dessous : une commande `skipSave` a quand même de vraies
    // entrées de forme (voir `CommandMeta`) et peut tout aussi bien avoir une rafale en attente.
    for (const entry of meta.entries) this.historyAnchors.delete(entry.shapeId);
    if (meta.skipSave) return;

    const resolved = meta.entries
      .map((metaEntry) => {
        const kind: SaveEvent["kind"] =
          changeKind === "redo"
            ? metaEntry.kind
            : metaEntry.kind === "create"
              ? "delete"
              : metaEntry.kind === "delete"
                ? "create"
                : "update";

        const confirmedShapeIfNew = changeKind === "undo" ? metaEntry.afterShape : metaEntry.beforeShape;
        const resultShape = this.shapeStore.getAll().find((s) => s.id === metaEntry.shapeId) ?? null;
        const shapeForSave = resultShape ?? confirmedShapeIfNew;
        return { shapeId: metaEntry.shapeId, kind, confirmedShapeIfNew, resultShape, shapeForSave };
      })
      // ne devrait pas arriver : une forme a forcément existé avant ou après
      .filter((e): e is typeof e & { shapeForSave: Shape } => e.shapeForSave != null);
    if (resolved.length === 0) return;

    if (!meta.batch) {
      const e = resolved[0]!;
      this.trackAndSave(e.shapeId, command, e.kind, meta.skipConfirm, e.confirmedShapeIfNew, e.resultShape, e.shapeForSave);
      return;
    }

    this.trackAndSaveBatch(
      resolved.map((e) => ({
        shapeId: e.shapeId,
        kind: e.kind,
        confirmedShapeIfNew: e.confirmedShapeIfNew,
        resultShape: e.resultShape,
      })),
      command,
      meta.skipConfirm,
      resolved.map((e) => e.shapeForSave),
    );
  }

  /**
   * Un succès confirme `command` et tout ce qui le précède dans la file (une réussite, même
   * tardive, est une vérité acquise — sauf si `command` a déjà été dépassée par une réussite plus
   * récente, auquel cas ce résultat tardif ne compte plus), SAUF si `skipConfirm` : dans ce cas
   * `confirmedShape` n'avance pas jusqu'à `resultShape`, même si `onSave` a bien réussi — un échec
   * ultérieur sur cette forme reviendra alors à l'état d'avant `command`, pas à son résultat.
   *
   * Cas particulier de `skipConfirm` quand la file se vide (`pendingCommands.length === 0`) : le
   * suivi (`ShapeSaveTracking`) n'est PAS supprimé de `this.tracking` comme il le serait
   * normalement — `confirmedShape` reste alors sciemment en retard sur l'état réel du store (qui,
   * lui, inclut déjà le résultat de `command`, appliqué optimistiquement). Le supprimer casserait
   * l'invariant "pas de suivi = le store reflète la dernière confirmation" : la PROCHAINE commande
   * sur cette forme doit retrouver cette même référence en retard (voir le `tracking.get(shapeId)`
   * plus bas, qui la réutilise telle quelle), pas une nouvelle recalculée depuis le store courant.
   * Le suivi ne redevient supprimable qu'à la prochaine confirmation réelle (succès sans
   * `skipConfirm`) ou au rollback complet d'un échec (voir plus bas, inchangé).
   *
   * Un échec ne déclenche un rollback visible (réinjection du JSON complet de `confirmedShape`,
   * suppression si `null`) que si `command` est toujours la plus récente pour cette forme : sinon
   * une modification plus récente est encore en vol ou a déjà réussi, et on se contente de retirer
   * `command` de la pile Annuler/Rétablir (elle n'a jamais été persistée, mais rien d'autre ne
   * change) — c'est cette modification plus récente qui décidera, à sa propre résolution, s'il faut
   * revenir en arrière visuellement. Le rollback complet retire aussi de la pile Annuler/Rétablir
   * toutes les autres commandes encore en vol pour cette forme (aucune n'aura jamais été
   * persistée).
   */
  private trackAndSave(
    shapeId: string,
    command: Command,
    kind: SaveEvent["kind"],
    skipConfirm: boolean,
    confirmedShapeIfNew: Shape | null,
    resultShape: Shape | null,
    shapeForSave: Shape,
  ): void {
    let tracking = this.tracking.get(shapeId);
    if (!tracking) {
      tracking = { confirmedShape: confirmedShapeIfNew, pendingCommands: [] };
      this.tracking.set(shapeId, tracking);
    }
    const entry: PendingEntry = { command };
    tracking.pendingCommands.push(entry);

    if (!this.onSave) return;
    const currentTracking = tracking;
    this.notify(shapeId, "pending");

    this.onSave({ shapes: [shapeForSave], kind }).then(
      () => {
        const index = currentTracking.pendingCommands.indexOf(entry);
        // Déjà retirée par une réussite plus récente (voir plus bas) : ce résultat tardif ne compte
        // plus, sans quoi il écraserait `confirmedShape` avec sa propre valeur devenue obsolète.
        if (index === -1) return;
        currentTracking.pendingCommands.splice(0, index + 1);
        if (!skipConfirm) currentTracking.confirmedShape = resultShape;
        if (currentTracking.pendingCommands.length === 0) {
          if (!skipConfirm) this.tracking.delete(shapeId);
          this.notify(shapeId, "success");
        }
      },
      () => {
        const index = currentTracking.pendingCommands.indexOf(entry);
        const isMostRecent = index !== -1 && index === currentTracking.pendingCommands.length - 1;
        if (!isMostRecent) {
          console.warn(
            `[onSave] échec ignoré pour la forme "${shapeId}" : une modification plus récente est en cours ou a déjà réussi.`,
          );
          // `command` elle-même n'a jamais été persistée (son propre `onSave` vient d'échouer) : on
          // la retire quand même de la pile Annuler/Rétablir tout de suite, même si on ne "revient
          // pas en arrière" visuellement (ce qui se passe encore en vol/déjà réussi le reste). Sans
          // ça, il resterait une entrée fantôme — annulable/rétablissable comme une vraie
          // modification alors qu'elle n'aura jamais été sauvegardée.
          if (index !== -1) {
            currentTracking.pendingCommands.splice(index, 1);
            this.history.discard(command);
          }
          return;
        }
        if (currentTracking.confirmedShape == null) this.shapeStore.remove(shapeId);
        else this.shapeStore.add(currentTracking.confirmedShape);
        for (const pending of currentTracking.pendingCommands) this.history.discard(pending.command);
        this.tracking.delete(shapeId);
        // Même raison que dans `runHistoryChange` : ce rollback vient de réécrire l'état "réel" de
        // la forme en dehors de `resolveHistoryBeforeShape` — une ancre encore en attente pour elle
        // (rafale `skipHistory` ouverte pendant que CETTE sauvegarde était en vol) décrirait sinon un
        // état devenu fantôme dès la prochaine commande réelle sur cette forme.
        this.historyAnchors.delete(shapeId);
        this.notify(shapeId, "error");
      },
    );
  }

  /**
   * Équivalent de `trackAndSave` pour un groupe de formes partageant UN seul appel `onSave` (voir
   * `runBatch`) — même logique de confirmation/rollback "plus récent l'emporte", appliquée
   * indépendamment PAR FORME (chacune garde sa propre `ShapeSaveTracking`), mais déclenchée par une
   * seule promesse partagée. Les objets `ShapeSaveTracking` de chaque forme sont capturés une fois
   * ici (`trackingByShapeId`), pas relus depuis `this.tracking` dans les callbacks — même raison que
   * `currentTracking` dans `trackAndSave` : la référence doit rester celle de CE suivi précis, même
   * si `this.tracking` a depuis été remplacée pour cette forme (rollback puis nouvelle commande).
   */
  private trackAndSaveBatch(
    entries: BatchSaveEntry[],
    command: Command,
    skipConfirm: boolean,
    shapesForSave: Shape[],
  ): void {
    const entry: PendingEntry = { command };
    const trackingByShapeId = new Map<string, ShapeSaveTracking>();
    for (const e of entries) {
      let tracking = this.tracking.get(e.shapeId);
      if (!tracking) {
        tracking = { confirmedShape: e.confirmedShapeIfNew, pendingCommands: [] };
        this.tracking.set(e.shapeId, tracking);
      }
      tracking.pendingCommands.push(entry);
      trackingByShapeId.set(e.shapeId, tracking);
    }

    if (!this.onSave) return;
    for (const e of entries) this.notify(e.shapeId, "pending");

    // `entries` porte normalement le même `kind` pour toutes ses formes (un seul geste/une seule
    // action de groupe — déplacement, verrouillage, suppression...), d'où un seul appel `onSave`.
    // Mais "diviser ici" sur une ligne (`createTokaMap.ts`, cas `"shape:line-split"`) produit un
    // batch à kinds MIXTES ("update" pour la ligne raccourcie, "create" pour le nouveau tronçon —
    // inversés en "update"/"delete" par `runHistoryChange` lors d'un Annuler/Rétablir). On regroupe
    // donc les entries par kind et on appelle `onSave` une fois PAR kind distinct présent dans le
    // groupe — un seul appel dans le cas courant (kind uniforme) — plutôt que de figer `kind` sur la
    // première entrée : sinon la forme au kind minoritaire recevrait à tort le kind de la première
    // (ex. la forme créée par un split traitée comme une simple mise à jour, ou "modifiée" au lieu
    // d'être supprimée lors de l'Annuler correspondant).
    const groups = new Map<SaveEvent["kind"], { entries: BatchSaveEntry[]; shapes: Shape[] }>();
    entries.forEach((e, i) => {
      const group = groups.get(e.kind) ?? { entries: [], shapes: [] };
      group.entries.push(e);
      group.shapes.push(shapesForSave[i]!);
      groups.set(e.kind, group);
    });

    for (const [kind, group] of groups) {
      this.onSave({ shapes: group.shapes, kind }).then(
        () => {
          for (const e of group.entries) {
            const tracking = trackingByShapeId.get(e.shapeId)!;
            const index = tracking.pendingCommands.indexOf(entry);
            if (index === -1) continue; // déjà dépassée par une réussite plus récente sur cette forme
            tracking.pendingCommands.splice(0, index + 1);
            if (!skipConfirm) tracking.confirmedShape = e.resultShape;
            if (tracking.pendingCommands.length === 0) {
              if (!skipConfirm) this.tracking.delete(e.shapeId);
              this.notify(e.shapeId, "success");
            }
          }
        },
        () => {
          for (const e of group.entries) {
            const tracking = trackingByShapeId.get(e.shapeId)!;
            const index = tracking.pendingCommands.indexOf(entry);
            const isMostRecent = index !== -1 && index === tracking.pendingCommands.length - 1;
            if (!isMostRecent) {
              console.warn(
                `[onSave] échec ignoré pour la forme "${e.shapeId}" : une modification plus récente est en cours ou a déjà réussi.`,
              );
              if (index !== -1) {
                tracking.pendingCommands.splice(index, 1);
                this.history.discard(command);
              }
              continue;
            }
            if (tracking.confirmedShape == null) this.shapeStore.remove(e.shapeId);
            else this.shapeStore.add(tracking.confirmedShape);
            for (const pending of tracking.pendingCommands) this.history.discard(pending.command);
            this.tracking.delete(e.shapeId);
            // Voir `trackAndSave` — même rollback, même raison d'invalider une ancre en attente.
            this.historyAnchors.delete(e.shapeId);
            this.notify(e.shapeId, "error");
          }
        },
      );
    }
  }

  private notify(shapeId: string, status: SaveStatusEvent["status"]): void {
    for (const listener of this.listeners) listener({ shapeId, status });
  }
}
